import Header from "../Components/Header/Header";
import Footer from "../Components/Footer/Footer";
import {useLocation, useNavigate} from "react-router-dom";
import React, { useEffect, useState, useRef } from "react";
import { isMobile } from "react-device-detect";
import { getWinesByCultivar, getWinesByStyle } from '../actions/ApiCalls';
import ProductFilter from "../Components/ProductFilter";
import './app.css';

function FilteredProducts() {
    const location = useLocation();
    const filters = location.state?.filters;
    const filterType = location.state?.filterType;
    const selectedCultivarNames = location.state?.selectedCultivarNames;
    const selectedStyleNames = location.state?.selectedStyleNames;
    const wineURL = 'https://vinimark.s3.amazonaws.com/web/';
    const customer_code = localStorage.getItem('customer_code');

    const [products, setProducts] = useState<any>([]);
    const [filteredProducts, setFilteredProducts] = useState<any>([]);
    const [loading, setLoading] = useState(false);
    const [filterMode, setFilterMode] = useState<'fixed' | 'scroll'>('fixed');
    const navigate = useNavigate();
    const filterRef = useRef<HTMLDivElement>(null);
    const [imageRatios, setImageRatios] = useState<{[key: string]: number}>({});

    const formatPrice = (price: number | string): string => {
        const numPrice = typeof price === 'string' ? parseFloat(price) : price;
        return `R${numPrice.toFixed(2)}`;
    };

    const isDesktop = !isMobile;

    const handleImageClick = (product: any) => {
        if (product) {
            navigate(`/our-wines/${product.brand.search_name}/${product.search_name}`);
        }
    };


    useEffect(() => {
        window.scrollTo(0,0)
        if (filters && filterType) {
            setLoading(true);

            const apiCall = filterType === 'cultivars'
                ? getWinesByCultivar(filters)
                : getWinesByStyle(filters);

            apiCall
                .then(response => {
                    setProducts(response.data.data);
                    setFilteredProducts(response.data.data);
                    setLoading(false);
                })
                .catch(error => {
                    console.error("Failed to fetch filtered products:", error);
                    setLoading(false);
                });
        }
    }, [filters, filterType]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setFilterMode('scroll');
                } else {
                    setFilterMode('fixed');
                }
            },
            { threshold: 0.1 }
        );

        if (filterRef.current) {
            observer.observe(filterRef.current);
        }

        return () => {
            if (filterRef.current) {
                observer.unobserve(filterRef.current);
            }
        };
    }, []);

    const handleFilterChange = (filteredResults: any[]) => {
        setFilteredProducts(filteredResults);
    }

    const calculateImageDimensions = (imageRatio: number, containerHeight: number) => {
        const maxHeight = containerHeight - 100; // 50px from top and bottom
        const maxWidth = '90%';

        if (imageRatio > 1.5) { // Taller image
            return {
                height: `${maxHeight}px`,
                width: 'auto',
                maxWidth
            };
        } else { // Wider image
            return {
                height: 'auto',
                width: maxWidth,
                maxHeight: `${containerHeight < 500 ? containerHeight * 0.6 : containerHeight * 0.7}px`

            };
        }
    };


    // In getDesktopLayout function, update the image container and image styles
    function getDesktopLayout() {
        return filteredProducts.map((product: any) => (
            <div
                key={product.id}
                style={{
                    flex: '1 1 calc(25% - 20px)',
                    textAlign: 'center',
                    paddingBottom: '10px',
                    maxHeight: '750px',
                    maxWidth: '400px',
                    cursor: 'pointer',
                }}
                onClick={() => handleImageClick(product.search_name)}
            >
                <div
                    style={{
                        width: '100%',
                        backgroundColor: '#f5f5f5',
                        height: '600px',
                        position: 'relative',
                        padding: '0 30px',
                        boxSizing: 'border-box',
                    }}
                >
                    <img
                        src={wineURL + product.image_path}
                        alt={product.name}
                        onLoad={(e) => {
                            const img = e.target as HTMLImageElement;
                            const ratio = img.naturalHeight / img.naturalWidth;
                            setImageRatios(prev => ({...prev, [product.id]: ratio}));
                        }}
                        style={{
                            position: 'absolute',
                            bottom: '50px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            ...calculateImageDimensions(imageRatios[product.id] || 1.5, 600),
                            objectFit: 'contain'
                        }}
                    />
                </div>
                {/* ... rest of the product info ... */}
                <p style={textLight}>{product?.brand?.name}</p>
                <p style={productName}>{product.name}</p>
                <p style={productYear}>{product.year}</p>
                {customer_code && product.price_bottle && (
                    <p style={priceText}>{formatPrice(product.price_bottle)}</p>
                )}
            </div>
        ));
    }
    const textLight: React.CSSProperties = {
        fontWeight: 400,
        marginBottom: 0,
        fontSize: '13px',
        lineHeight: '16.9px',
        width: '100%'

    };

    const productName: React.CSSProperties = {
        fontWeight: 600,
        textTransform: 'uppercase',
        marginTop: 0,
        marginBottom: 0,
        fontSize: '13px',
        lineHeight: '16.9px',
    };

    const productYear: React.CSSProperties = {
        fontWeight: 600,
        textTransform: 'uppercase',
        marginTop: 0,
        marginBottom: 0,
        fontSize: '13px',
        lineHeight: '16.9px',
    };

    const priceText: React.CSSProperties = {
        fontSize: '13px',
        lineHeight: '16.9px',
        marginTop: 0,
        fontWeight: 400,
    };

    // In getMobileLayout function, update the image container and image styles
    function getMobileLayout() {
        return filteredProducts.map((product: any) => (
            <div
                key={product.id}
                style={{
                    width: '100%',
                    textAlign: 'center',
                    paddingBottom: '30px',
                    maxHeight: '750px',
                }}
            >
                <div
                    style={{
                        width: '100%',
                        backgroundColor: '#f5f5f5',
                        height: '400px',
                        position: 'relative',
                        padding: '0 30px',
                        boxSizing: 'border-box',
                    }}
                    onClick={() => handleImageClick(product.search_name)}
                >
                    <img
                        src={wineURL + product.image_path}
                        alt={product.name}
                        onLoad={(e) => {
                            const img = e.target as HTMLImageElement;
                            const ratio = img.naturalHeight / img.naturalWidth;
                            setImageRatios(prev => ({...prev, [product.id]: ratio}));
                        }}
                        style={{
                            position: 'absolute',
                            bottom: '50px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            ...calculateImageDimensions(imageRatios[product.id] || 1.5, 400),
                            objectFit: 'contain'
                        }}
                    />
                </div>
                <p style={textLight}>{product?.brand?.name}</p>
                <p style={productName}>{product.name}</p>
                <p style={productYear}>{product.year}</p>
                {customer_code && product.price_bottle && (
                    <p style={priceText}>{formatPrice(product.price_bottle)}</p>
                )}
            </div>
        ));
    }

    return (
        <div className="page-layout" style={{marginBottom: '-80px'}}>
            <Header />
            <main className="main-content">
                <div style={{display: 'flex', flexDirection: window.innerWidth > 575 ? 'row' : 'column', paddingLeft: window.innerWidth > 575 ? '0' : '20px', marginTop: '140px' , marginBottom: '72px'}}>
                    <div style={{flex: 1}}></div>
                    <div style={{flex: 1}}>
                        {filterType === 'cultivars' ?
                            <div style={{marginLeft: '26px'}}>
                                <p style={{fontSize: '13px', lineHeight: '16.9px', textAlign: 'left', fontWeight: 600, marginTop: 0, marginBottom: 0}}>Filtered by Cultivars: </p>
                                <p style={{fontSize: '13px', lineHeight: '16.9px', textAlign: 'left', fontWeight: 400, marginTop: 0, marginBottom: 0}}>
                                    {selectedCultivarNames?.join(', ') || ''}
                                </p>
                            </div> :
                            <div style={{marginLeft: '26px'}}>
                                <p style={{fontSize: '13px', lineHeight: '16.9px', textAlign: 'left', fontWeight: 600, marginTop: 0, marginBottom: 0}}>Filtered by Styles: </p>
                                <p style={{fontSize: '13px', lineHeight: '16.9px', textAlign: 'left', fontWeight: 400, marginTop: 0, marginBottom: 0}}>
                                    {selectedStyleNames?.join(', ') || ''}
                                </p>
                            </div>
                        }
                    </div>
                </div>
                <div>
                    {filteredProducts !== undefined && filteredProducts.length > 0 ? (
                            <div style={{display: 'flex', justifyContent: 'center'}}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        justifyContent: 'flex-start',
                                        gap: '40px 20px',
                                        padding: '30px 20px',
                                        margin: '0 auto'
                                    }}
                                >
                                    {(isDesktop ? getDesktopLayout() : getMobileLayout())}
                                </div>
                            </div>

                    ) : (
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                            {loading ? 'Loading products...' : 'No products found'}
                        </div>
                    )}
                </div>

                <div className="filter-container" ref={filterRef}>
                    <ProductFilter
                        brands={products}
                        onFilterChange={handleFilterChange}
                        mode={filterMode}
                    />
                </div>
            </main>
            <div>
                <Footer />
            </div>

        </div>
    );
}

export default FilteredProducts;
